import { getIxdForwardRuns } from "@/apis/ixd-forward/route";
import { isBetaFeatureEnabled } from "@/components/beta-feature";
import { useQuery } from "@tanstack/react-query";

// ============================================
// useWorkbookForwardState
// 이 워크북의 IXD 태깅이 지금 어떤 상태인가.
//
// 도는 동안 표를 열면 값 대사로 옮긴 태그만 보인다 — LLM 보완분은 마지막
// 단계가 끝날 때 한꺼번에 쓰이므로, 화면만 봐서는 "아직 덜 됐다"와 "이게
// 최종이다"가 똑같이 보인다. 그 둘을 갈라주는 표시의 근거.
//
// 실패도 같이 알려준다. 파이프라인이 죽으면 워크북은 표만 있고 태그는 없는
// 채로 남는데, 그 모습이 "아직 도는 중"과 구분되지 않는다. 실제로 DB 커넥션
// 고갈로 1분 만에 죽은 run 을 도는 중으로 착각한 적이 있다 — 알려주지 않는
// 실패가 제일 비싸다.
//
// 표시 전용이라 아무것도 막지 않는다. 베타를 안 켠 사용자는 이 기능을 시작할
// 수조차 없으므로 조회를 걸지 않는다.
// ============================================

const POLL_INTERVAL_MS = 5000;

export type WorkbookForwardState =
  | { kind: "idle" }
  | { kind: "forwarding" }
  | { kind: "failed"; error: string | null };

const IDLE: WorkbookForwardState = { kind: "idle" };

const isRunning = (status: string) =>
  status === "PENDING" || status === "RUNNING";

export const useWorkbookForwardState = (
  workbookId: string | null,
): WorkbookForwardState => {
  const beta = isBetaFeatureEnabled();

  const { data } = useQuery({
    queryKey: ["ixd-forward-runs"],
    queryFn: getIxdForwardRuns,
    enabled: beta && workbookId !== null,
    // 도는 동안만 폴링한다 — 끝나거나 죽으면 멈춘다.
    refetchInterval: (query) => {
      const runs = query.state.data?.runs ?? [];
      const active = runs.some((run) => isRunning(run.status));
      return active ? POLL_INTERVAL_MS : false;
    },
  });

  if (!beta || workbookId === null) {
    return IDLE;
  }

  // run 하나가 워크북 하나를 만들므로 워크북당 run 도 하나다.
  for (const run of data?.runs ?? []) {
    if (run.priorWorkbookId !== workbookId) continue;
    if (isRunning(run.status)) return { kind: "forwarding" };
    if (run.status === "FAILED") return { kind: "failed", error: run.error };
  }
  return IDLE;
};
